import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTrackModal } from '../../../hooks/useTrackModal';
import styles from './Modal.module.css';

/**
 * Modal Component
 *
 * A reusable modal/dialog component with animations and nested modal support
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Control modal visibility
 * @param {function} props.onClose - Close handler
 * @param {boolean} props.closeOnBackdrop - Close when clicking backdrop (default: true)
 * @param {boolean} props.closeOnEsc - Close when pressing ESC (default: true)
 * @param {boolean} props.preventBodyScroll - Prevent body scroll when open (default: true)
 * @param {string} props.size - Modal size: 'sm' | 'md' | 'lg' | 'xl' | 'full'
 * @param {string} props.variant - Modal variant: 'default' | 'danger' | 'warning'
 * @param {string} props.title - Modal title (shorthand for header)
 * @param {boolean} props.showCloseButton - Show close button (default: true)
 * @param {number} props.zIndex - Custom z-index for nested modals
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.theme - Theme: 'dark' | 'light' (for theme support)
 * @param {string} props.trackAs - Name recorded in usage tracking (falls back to title)
 * @param {React.ReactNode} props.children - Modal content
 */
const Modal = ({
  isOpen = false,
  onClose,
  closeOnBackdrop = true,
  closeOnEsc = true,
  preventBodyScroll = true,
  size = 'md',
  variant = 'default',
  title = null,
  showCloseButton = true,
  zIndex = null,
  className = '',
  theme = 'dark',
  trackAs = null,
  children,
}) => {
  useTrackModal(trackAs || (typeof title === 'string' ? title : 'Unnamed modal'), isOpen);

  // Handle ESC key
  const handleEscKey = useCallback(
    (event) => {
      if (closeOnEsc && event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onClose?.();
      }
    },
    [closeOnEsc, isOpen, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (closeOnBackdrop && event.target === event.currentTarget) {
      onClose?.();
    }
  };

  // Manage body scroll and ESC key listener
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      if (preventBodyScroll) {
        document.body.style.overflow = 'hidden';
      }

      // Add ESC key listener
      if (closeOnEsc) {
        document.addEventListener('keydown', handleEscKey);
      }
    }

    return () => {
      // Restore body scroll
      if (preventBodyScroll) {
        document.body.style.overflow = '';
      }

      // Remove ESC key listener
      if (closeOnEsc) {
        document.removeEventListener('keydown', handleEscKey);
      }
    };
  }, [isOpen, preventBodyScroll, closeOnEsc, handleEscKey]);

  if (!isOpen) return null;

  const backdropClasses = [styles.backdrop, styles[variant]].filter(Boolean).join(' ');

  const modalClasses = [styles.modal, styles[size], className]
    .filter(Boolean)
    .join(' ');

  const customStyle = zIndex ? { zIndex } : {};

  return createPortal(
    <div
      className={backdropClasses}
      onClick={handleBackdropClick}
      style={customStyle}
      data-theme={theme}
    >
      <div className={modalClasses} onClick={(e) => e.stopPropagation()}>
        {/* Render title if provided (shorthand) */}
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            {showCloseButton && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close modal"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Render children (can include ModalHeader, ModalBody, ModalFooter) */}
        {children}

        {/* Show close button at top-right if no title */}
        {!title && showCloseButton && (
          <button
            type="button"
            className={styles.closeButtonAbsolute}
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
